import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './Activities.css';

function FadeSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add('visible'); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={`fade-in ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function PhotoGallery({ images, eventTitle }) {
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
    if (lightbox !== null) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [lightbox]);

  const prev = () => setLightbox(i => (i - 1 + images.length) % images.length);
  const next = () => setLightbox(i => (i + 1) % images.length);

  return (
    <>
      <div className="photo-gallery">
        {images.map((src, i) => (
          <button key={i} className="photo-gallery__thumb" onClick={() => setLightbox(i)}>
            <img src={src} alt={`${eventTitle} — photo ${i + 1}`} loading="lazy" />
            <div className="photo-gallery__overlay">🔍</div>
          </button>
        ))}
      </div>

      {lightbox !== null && (
        <div className="lightbox" onClick={() => setLightbox(null)}>
          <button className="lightbox__close" onClick={() => setLightbox(null)}>✕</button>
          <button className="lightbox__nav lightbox__nav--prev" onClick={e => { e.stopPropagation(); prev(); }}>‹</button>
          <div className="lightbox__img-wrap" onClick={e => e.stopPropagation()}>
            <img src={images[lightbox]} alt={`${eventTitle} — photo ${lightbox + 1}`} />
            <div className="lightbox__caption">{eventTitle} — {lightbox + 1} / {images.length}</div>
          </div>
          <button className="lightbox__nav lightbox__nav--next" onClick={e => { e.stopPropagation(); next(); }}>›</button>
        </div>
      )}
    </>
  );
}

const events = [
  {
    id: 'rally-to-kumari-2026',
    icon: '🏍️',
    tag: 'World Environment Day',
    title: 'Rally to Kumari — World Environment Day 2026',
    subtitle: 'Turning Awareness into Action for a Greener Tomorrow',
    date: '5–6 Jun 2026',
    location: 'Chennai to Kanyakumari, Tamil Nadu',
    color: '#1a5c2e',
    accent: 'linear-gradient(135deg, #f0faf4, #d6f0e0)',
    desc: 'Y2K Global Nexus Foundation, in association with the Tamil Nadu Forest Department and Suzuki, organised a mega two-day World Environment Day initiative. Day 1 (Jun 5) featured a World Environment Day Awareness Rally from Suzuki Showroom, Ambattur, Chennai to Kanyakumari. Day 2 (Jun 6) was a Tree Planting Mega Event held at Anjunar Anna Stadium, Nagercoil — bringing together athletes, volunteers, and officials under the banner: One Earth, One Family, One Future.',
    images: [
      '/images/activities/rally-to-kumari-world-environment-day-poster.jpeg',
      '/images/activities/rally-to-kumari-tree-planting-mega-event-briefing.jpeg',
      '/images/activities/rally-to-kumari-tree-planting-mega-event-group.jpeg',
      '/images/activities/calltree-y2k-world-environment-day-2026-brochure.jpeg',
      '/images/activities/rotary-nagercoil-env-day-celebration-invite.jpeg',
      '/images/activities/rotary-nagercoil-env-day-calltree-team-group-photo.jpeg',
      '/images/activities/rotary-nagercoil-env-day-calltree-team-pose.jpeg',
      '/images/activities/rotary-nagercoil-env-day-calltree-team-seated.jpeg',
      '/images/activities/rotary-nagercoil-env-day-krishnakumar-address.jpeg',
      '/images/activities/rotary-nagercoil-env-day-audience-session.jpeg',
      '/images/activities/rotary-nagercoil-env-day-felicitation-ceremony.jpeg',
    ],
  },
  {
    id: 'kanyakumari-marathon-2026',
    icon: '🏃',
    tag: 'Marathon & Sports',
    title: 'ஆரோக்கியம் கன்னியாகுமரி மாரத்தான் 2026',
    subtitle: 'Run for Health — Kanyakumari Marathon',
    date: '17 May 2026',
    location: 'Kanyakumari District, Tamil Nadu',
    color: '#1a5c2e',
    accent: 'linear-gradient(135deg, #f0faf4, #d6f0e0)',
    desc: 'Y2K Global Nexus Foundation proudly supported and co-sponsored the "ஆரோக்கியம் — Run for Health" Kanyakumari Marathon 2026. Presented by Rojavanam International School and co-presented by St. Joseph University, the event brought together runners across categories with Y2K awarding prize money and trophies to winners. Media covered live by Puthiya Thalaimurai.',
    images: [
      '/images/activities/WhatsApp Image 2026-05-17 at 15.46.37.jpeg',
      '/images/activities/WhatsApp Image 2026-05-17 at 10.01.51.jpeg',
      '/images/activities/WhatsApp Image 2026-05-17 at 09.43.19.jpeg',
      '/images/activities/WhatsApp Image 2026-05-17 at 10.00.55.jpeg',
    ],
  },
  {
    id: 'first-voter-awareness-kanyakumari',
    icon: '🗳️',
    tag: 'First Voter Campaign',
    title: 'தலைவன் யாரென்று நீ சொல்லு',
    subtitle: 'First-Time Voter Awareness — Kanyakumari',
    date: '20 Apr 2026',
    location: 'Kanyakumari District, Tamil Nadu (Lat: 8.329°N, Long: 77.416°E)',
    color: '#1a5c2e',
    accent: 'linear-gradient(135deg, #f0faf4, #d6f0e0)',
    desc: '"தலைவன் யாரென்று நீ சொல்லு" — You decide who the leader is. Y2K Global Nexus organised a first-time voter awareness campaign in Kanyakumari, bringing together hundreds of young students and athletes for an evening session on the importance of voting, civic responsibility, and the power of the youth vote. The event reached a packed audience of first-time eligible voters.',
    images: [
      '/images/activities/WhatsApp Image 2026-04-21 at 10.05.37.jpeg',
      '/images/activities/WhatsApp Image 2026-04-21 at 10.05.38.jpeg',
      '/images/activities/WhatsApp Image 2026-04-21 at 10.05.38 (1).jpeg',
      '/images/activities/WhatsApp Image 2026-04-21 at 10.05.39.jpeg',
      '/images/activities/WhatsApp Image 2026-04-21 at 10.05.39 (1).jpeg',
      '/images/activities/WhatsApp Image 2026-04-21 at 10.05.39 (2).jpeg',
      '/images/activities/WhatsApp Image 2026-04-21 at 10.05.40.jpeg',
    ],
  },
  {
    id: 'happy-green-year-2023',
    icon: '🎋',
    tag: 'New Year Special',
    title: 'Happy Green Year 2023',
    subtitle: 'Midnight Tree Planting at Chennai',
    date: '21–22 Dec 2022',
    location: 'College of Food & Dairy Technology, Chennai',
    color: '#1a5c2e',
    accent: 'linear-gradient(135deg, #f0faf4, #d6f0e0)',
    desc: 'Ringing in the new year the green way — a midnight tree planting ceremony organised in coordination with the Chennai Forest Division and academic institutions. Officials, students, and volunteers gathered to plant saplings at the stroke of midnight.',
    images: [
      '/images/activities/WhatsApp Image 2026-04-06 at 16.52.50.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.52.51.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.52.53.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.03.jpeg',
    ],
  },
  {
    id: 'propose-with-purpose-2024',
    icon: '❤️',
    tag: "Valentine's Day",
    title: 'Propose with Purpose',
    subtitle: "Valentine's Day Tree Gifting — Feb 2024",
    date: '14 Feb 2024',
    location: 'Central Nursery, Karasangal, Padappai, Chennai',
    color: '#c9a227',
    accent: 'linear-gradient(135deg, #fffbf0, #fff0d0)',
    desc: 'Why propose in red when you can propose in green? Couples and families celebrated Valentine\'s Day by gifting tree saplings — turning a day of love into a living, growing memory. Registration-based event with prizes for the most trees planted by one person.',
    images: [
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.50.jpeg',
    ],
  },
  {
    id: 'green-diwali-2024',
    icon: '🪔',
    tag: 'Diwali Campaign',
    title: 'Green Diwali 2024',
    subtitle: 'Pollution Free — Solution Tree',
    date: '31 Oct 2024',
    location: 'Tambaram Special Range, Chennai Forest Division',
    color: '#e07b00',
    accent: 'linear-gradient(135deg, #fffaf0, #fff0d0)',
    desc: '"Pollution Free... Solution Tree" — a biker rally and tree planting initiative on the day of Diwali. Riders assembled at the Central Nursery, Karasangal and rode 16 km to the finish point, planting trees along the route. Organised with the Tambaram Forest Range, Chennai.',
    images: [
      '/images/activities/WhatsApp Image 2026-04-06 at 16.52.56.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.21.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.25.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.44.jpeg',
    ],
  },
  {
    id: 'green-diwali-2025',
    icon: '🌿',
    tag: 'Diwali Campaign',
    title: 'Green Diwali 2025',
    subtitle: 'Burst Crackers on Oct 20 — Plant a Tree on Oct 21',
    date: '20–21 Oct 2025',
    location: 'MVM Nagar, Tamil Nadu & Pan India via CallTree',
    color: '#2d8a4e',
    accent: 'linear-gradient(135deg, #f0faf4, #d6f0e0)',
    desc: 'CallTree\'s Green Diwali 2025 campaign: burst crackers on Diwali day and plant a tree species the next day. Participants registered, planted, photographed, and got rewarded. The initiative ran across Tamil Nadu with volunteers braving rain to plant saplings.',
    images: [
      '/images/activities/WhatsApp Image 2026-04-06 at 16.52.48.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.52.59.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.52.58.jpeg',
    ],
  },
  {
    id: 'van-mahotsav-2024',
    icon: '🌳',
    tag: 'Van Mahotsav',
    title: 'Van Mahotsav Week 2024',
    subtitle: 'Festival of Life — Tree Plantation Drive',
    date: '1–7 Jun 2024',
    location: 'Indian Oil, Athipattu, Chennai (Lat: 13.257°N)',
    color: '#1a5c2e',
    accent: 'linear-gradient(135deg, #eaf7ef, #d0f0db)',
    desc: 'Celebrating Van Mahotsav — the annual festival of trees — with a plantation drive at Indian Oil Corporation, Athipattu. Officers and employees planted saplings together in coordination with the Nambiyarambalam Special Range, Chennai Forest Division.',
    images: [
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.28.jpeg',
    ],
  },
  {
    id: 'ek-ped-maa-ke-naam',
    icon: '🌱',
    tag: '#Plant4Mother',
    title: 'Ek Ped Maa Ke Naam',
    subtitle: 'Tamil Nadu Forest Dept — Green Tamil Nadu Mission',
    date: 'Aug–Sep 2024',
    location: 'Schools across Chennai & Kancheepuram District',
    color: '#4a9eda',
    accent: 'linear-gradient(135deg, #f0f7ff, #dceefa)',
    desc: 'The national "Ek Ped Maa Ke Naam" (#Plant4Mother) campaign came alive in Tamil Nadu schools. Forest officials and volunteers visited Alwin International School, Government High School Kundal Kazhani (Kancheepuram), and other institutions — planting trees and inspiring the next generation.',
    images: [
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.12.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.05.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.25.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.52.45.jpeg',
    ],
  },
  {
    id: 'india-tree-plantation-july',
    icon: '🏫',
    tag: 'School Campaign',
    title: 'India Tree Plantation Campaign',
    subtitle: '"Ek Ped Maa Ke Naam" — 1st to 12th July',
    date: 'Jul 2024',
    location: 'Schools across Chennai',
    color: '#2d8a4e',
    accent: 'linear-gradient(135deg, #f0faf4, #d6f0e0)',
    desc: 'The India Tree Plantation Campaign "Celebrating Festival of Life" reached school campuses with enthusiastic students participating in tree planting alongside Forest Range Officers. Children eagerly planted saplings and posed with their young trees.',
    images: [
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.08.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.10.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.07.jpeg',
    ],
  },
  {
    id: 'birthday-tree-planting',
    icon: '🎂',
    tag: 'Birthday Trees',
    title: 'Birthday Tree Planting',
    subtitle: 'Plant a Tree — Celebrate Life',
    date: 'Ongoing',
    location: 'Madukarai Park, Nolambur, Chennai',
    color: '#c9a227',
    accent: 'linear-gradient(135deg, #fffbf0, #fff5d6)',
    desc: 'First breath is the first birth — birthday is a breathe day too. CallTree encourages individuals to plant a tree on their birthday instead of (or alongside) conventional celebrations. Yathunadhan\'s birthday tree planting at Madukarai Park is one of many such living gifts.',
    images: [
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.32.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.37.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.38.jpeg',
    ],
  },
  {
    id: 'inner-wheel-nagercoil',
    icon: '📢',
    tag: 'Awareness Session',
    title: 'Inner Wheel Club — Nagercoil',
    subtitle: 'Environmental Education & Miyawaki Forest Development',
    date: '24 Jan 2026',
    location: 'Kendriya Vidhyalaya, Konam, Nagercoil, Kanyakumari',
    color: '#4a9eda',
    accent: 'linear-gradient(135deg, #f0f7ff, #dceefa)',
    desc: 'A special initiative by the Inner Wheel Club of Nagercoil Sagaram on Environmental Education, Biodiversity, Miyawaki Forest Development and the importance of tree planting — inaugurated by the Principal of Kendriya Vidhyalaya Nagercoil.',
    images: [
      '/images/activities/WhatsApp Image 2026-04-06 at 16.52.55.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.15.jpeg',
    ],
  },
  {
    id: 'rotary-nagercoil',
    icon: '🤝',
    tag: 'Partnership',
    title: 'Rotary Club of Nagercoil',
    subtitle: 'Collaborative Environmental Awareness Meet',
    date: '2024',
    location: 'Rotary Club Hall, Nagercoil',
    color: '#1a5c2e',
    accent: 'linear-gradient(135deg, #f0faf4, #d6f0e0)',
    desc: 'Y2K Global Nexus partnered with the Rotary Club of Nagercoil for a community environmental awareness session, bringing together civic leaders, volunteers, and citizens to discuss environmental action and tree-planting initiatives.',
    images: [
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.16.jpeg',
    ],
  },
  {
    id: 'srm-national-green-corps',
    icon: '🏖️',
    tag: 'National Green Corps',
    title: 'National Green Corps — SRM University',
    subtitle: 'River Bank Plantation with Students',
    date: '2024',
    location: 'River Bank, Tamil Nadu (with SRM Institute of Science & Technology)',
    color: '#2d8a4e',
    accent: 'linear-gradient(135deg, #eaf7ef, #d0f0db)',
    desc: 'In collaboration with the National Green Corps, SRM Institute of Science & Technology and Shri College of Engineering organised a river bank plantation event. Students, Forest officials, and faculty planted saplings along the river, combining environmental education with hands-on action.',
    images: [
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.18.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.46.jpeg',
    ],
  },
  {
    id: 'mega-rally-flyover',
    icon: '🟢',
    tag: 'Mega Drive',
    title: 'CallTree Mega Plantation Rally',
    subtitle: 'Green Warriors — Under the Flyover',
    date: '2024',
    location: 'Outer Ring Road / Flyover Zone, Chennai',
    color: '#1a5c2e',
    accent: 'linear-gradient(135deg, #f0faf4, #d6f0e0)',
    desc: 'Dozens of CallTree volunteers in green T-shirts assembled under a flyover for a mass tree planting rally alongside Forest Department officials. The drive was a powerful show of green solidarity — transforming urban infrastructure zones into green corridors.',
    images: [
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.20.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.41.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.16.jpeg',
    ],
  },
  {
    id: 'kanyakumari-tree-drive',
    icon: '🏔️',
    tag: 'Tree Planting Drive',
    title: 'Kanyakumari District Tree Drive',
    subtitle: 'Forest Dept Drive with Community',
    date: '2024',
    location: 'Kanyakumari District, Tamil Nadu',
    color: '#2d8a4e',
    accent: 'linear-gradient(135deg, #eaf7ef, #d0f0db)',
    desc: 'A large-scale tree planting drive at the southern tip of India — Kanyakumari district. CallTree volunteers, Forest officials, school students, and community members gathered against a stunning rocky hill backdrop to plant native tree species.',
    images: [
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.42.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.48.jpeg',
    ],
  },
  {
    id: 'community-tree-drives',
    icon: '🌾',
    tag: 'Community Drives',
    title: 'Community Tree Planting Drives',
    subtitle: 'With Forest Officials Across Tamil Nadu',
    date: 'Ongoing',
    location: 'Various locations across Tamil Nadu',
    color: '#1a5c2e',
    accent: 'linear-gradient(135deg, #f0faf4, #d6f0e0)',
    desc: 'Regular tree planting drives conducted in coordination with local Forest Department officers, politicians, municipal officials, and community volunteers across Tamil Nadu — from urban neighbourhoods to rural panchayat schools.',
    images: [
      '/images/activities/WhatsApp Image 2026-04-06 at 16.52.40.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.52.41.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.52.43.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.52.45.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.30.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.35.jpeg',
    ],
  },
  {
    id: 'efi-award-2024',
    icon: '🏆',
    tag: 'Recognition',
    title: 'EFI Madras Maanikkangal Award 2024',
    subtitle: 'Mr. Krishna Kumar — Green Governance',
    date: '2024',
    location: 'Chennai',
    color: '#c9a227',
    accent: 'linear-gradient(135deg, #fffbf0, #fff5d6)',
    desc: 'The Environmentalist Foundation of India honoured Mr. Krishna Kumar with the prestigious "Indians for India — Madras Maanikkangal 2024" award for Green Governance — recognising his unwavering commitment and extraordinary efforts toward the betterment of Madras and her environment.',
    images: [
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.27.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.13.jpeg',
      '/images/activities/WhatsApp Image 2026-04-06 at 16.53.34.jpeg',
    ],
  },
];

export default function Activities() {
  return (
    <div className="activities-page">
      {/* HERO */}
      <section className="page-hero activities-hero">
        <div className="activities-hero__bg" />
        <div className="page-hero__overlay" />
        <div className="container page-hero__content">
          <span className="tag" style={{ background: 'rgba(255,255,255,0.15)', color: 'var(--gold-light)' }}>
            Our Events & Activities
          </span>
          <h1 className="page-hero__title">
            Real Events,<br />Real Impact
          </h1>
          <p className="page-hero__sub">
            From midnight new year plantations to Diwali biker rallies — every event we've conducted
            is a living proof that environmental action is possible anywhere, anytime.
          </p>
          <div className="activities-hero__stats">
            <div><strong>17+</strong><span>Events</span></div>
            <div><strong>52</strong><span>Photos</span></div>
            <div><strong>Tamil Nadu</strong><span>& Beyond</span></div>
          </div>
        </div>
      </section>

      {/* EVENTS GRID */}
      <section className="act-events-full section-padding">
        <div className="container">
          {events.map((ev, i) => (
            <FadeSection key={ev.id} delay={0}>
              <div className={`act-event-full ${i % 2 === 1 ? 'act-event-full--reverse' : ''}`}>
                {/* Info panel */}
                <div className="act-event-full__info">
                  <div className="act-event-full__meta">
                    <span className="act-event-full__tag" style={{ background: ev.accent, color: ev.color }}>{ev.tag}</span>
                    <span className="act-event-full__date-pill">📅 {ev.date}</span>
                  </div>
                  <div className="act-event-full__icon" style={{ color: ev.color }}>{ev.icon}</div>
                  <h2 className="act-event-full__title" style={{ color: ev.color }}>{ev.title}</h2>
                  <div className="act-event-full__subtitle">{ev.subtitle}</div>
                  <p className="act-event-full__desc">{ev.desc}</p>
                  <div className="act-event-full__location">
                    <span>📍</span> {ev.location}
                  </div>
                </div>

                {/* Gallery panel */}
                <div className="act-event-full__gallery" style={{ background: ev.accent }}>
                  <PhotoGallery images={ev.images} eventTitle={ev.title} />
                  {ev.images.length > 1 && (
                    <div className="act-event-full__photo-count">{ev.images.length} photos — click to view</div>
                  )}
                </div>
              </div>
            </FadeSection>
          ))}
        </div>
      </section>

      {/* VOLUNTEER */}
      <section className="act-volunteer">
        <div className="container">
          <FadeSection>
            <div className="act-volunteer__inner">
              <div>
                <h2>Become a Y2K <span className="gold-text">Field Champion</span></h2>
                <p>
                  Join our army of green warriors. Volunteer for tree planting, clean drives,
                  awareness campaigns, and help us build a better India — and a better world.
                </p>
              </div>
              <Link to="/contact" className="btn-gold">🤝 Volunteer Now</Link>
            </div>
          </FadeSection>
        </div>
      </section>
    </div>
  );
}
